import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { MLButton } from 'components/Button/Button';
import useGetDigestReportsListId from 'hooks/DigestReports/useGetDigestReportsListId';
import { LoadingOverlay } from 'pages/InteractiveMap/components/LoadingOverlay';

const formatDate = (dateString?: string | null, includeTime = true) => {
  if (!dateString) return '';
  return dayjs(dateString).format(includeTime ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY');
};
const formatSide = (side?: string) => {
  if (side === 'L') return '🡪L';
  if (side === 'R') return 'R🡨';
  return '';
};

const DigestReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  let { slug, id } = useParams();

  const { data: digestData, isLoading } = useGetDigestReportsListId({
    slug: slug || '',
    isRefectNeeded: false,
    id: id || '',
  });
  const data = digestData?.json_snapshot;

  if (isLoading || !data) {
    const mapHeight = window.innerHeight - 100;
    return (
      <div className="card">
        <div className="card-body position-relative">
          <LoadingOverlay mapHeight={mapHeight} />
        </div>
      </div>
    );
  }
  return (
    <div className="page-content bg-light">
      <Container fluid>
        <Card>
          <CardHeader>
            <Row className="align-items-center mb-3">
              <Col lg={9}>
                <h3 className="card-title mb-0 fw-bold" style={{ color: '#6a37d4' }}>
                  TROJAN SYSTEMS
                </h3>
                <h4 className="fw-bold">{data?.digest_meta?.digest_type}</h4>
              </Col>
              <Col lg={3} className="d-flex justify-content-end">
                <MLButton
                  innerText={t('VisitReports.page-header.pdf-button')}
                  color="purple"
                  fontSize={18}
                  icon="ri-file-pdf-fill fs-16"
                  isGhost
                  onClick={() => {
                    navigate(`/digest-report/${slug}/${id}/check/`);
                  }}
                />
              </Col>
            </Row>

            <Row className="bg-light p-3 rounded mb-3">
              <Col md={6}>
                <div>
                  <strong>{t('DigestReport.page-header.Client')}:</strong> {data?.digest_meta?.client}
                </div>
                <div>
                  <strong>{t('DigestReport.page-header.Site')}:</strong> {data?.digest_meta?.site.name}
                </div>
                <div>
                  <strong>{t('DigestReport.page-header.Address')}:</strong> {data?.digest_meta?.site.address}
                </div>
              </Col>
              <Col md={6} className="text-md-end">
                <div>
                  <strong>{t('DigestReport.page-header.Generated')}:</strong>
                  {formatDate(data?.digest_meta?.generated_at)}
                </div>
                <div>
                  <strong>{t('DigestReport.page-header.Prepared')}:</strong> {data?.digest_meta?.prepared_for}
                </div>
              </Col>
            </Row>

            <p className="text-muted fst-italic mb-0">{data?.digest_meta?.notes[0]}</p>
          </CardHeader>

          <CardBody>
            <div className="mb-3">
              <h5 className="fw-bold pb-2">{t('DigestReport.page-body.Title')}</h5>
              <ul className="list-unstyled">
                {(data?.summary?.captures?.total || 0) > 0 && (
                  <li>
                    <strong>{t('DigestReport.page-body.Captures')}: </strong>
                    {t('DigestReport.page-body.CapturesData', {
                      total: data?.summary?.captures?.total,
                      trap_count: data?.summary?.captures?.trap_count,
                      mct: data?.summary?.captures?.by_type?.MCT || 0,
                      smst: data?.summary?.captures?.by_type?.SMST || 0,
                    })}
                  </li>
                )}
                {(data?.summary?.triggers_without_capture?.total || 0) > 0 && (
                  <li>
                    <strong>{t('DigestReport.page-body.TriggersWithoutCapture')}: </strong>
                    {t('DigestReport.page-body.TriggersWithoutCaptureData', {
                      total: data?.summary?.triggers_without_capture?.total,
                      trap_count: data?.summary?.triggers_without_capture?.trap_count,
                      mct: data?.summary?.triggers_without_capture?.by_type?.MCT || 0,
                      srst: data?.summary?.triggers_without_capture?.by_type?.SRST || 0,
                    })}
                  </li>
                )}
                {(data?.summary?.bait_modules_with_new_activity?.total || 0) > 0 && (
                  <li>
                    <strong>{t('DigestReport.page-body.BaitModulesWithNewActivity')}: </strong>
                    {t('DigestReport.page-body.BaitModulesWithNewActivityData', {
                      total: data?.summary?.bait_modules_with_new_activity?.total,
                      consumption: data?.summary?.bait_modules_with_new_activity?.consumption,
                      visits_without_consumption:
                        data?.summary?.bait_modules_with_new_activity?.visits_without_consumption,
                    })}
                  </li>
                )}
                {(data?.summary?.technical_issues?.total_devices || 0) > 0 && (
                  <li>
                    <strong>{t('DigestReport.page-body.TechnicalIssues')}: </strong>{' '}
                    {t('DigestReport.page-body.TechnicalIssuesData', {
                      total_devices: data?.summary?.technical_issues?.total_devices,
                      offline: data?.summary?.technical_issues?.offline,
                      signal_degraded: data?.summary?.technical_issues?.signal_degraded,
                      intermittent: data?.summary?.technical_issues?.intermittent,
                      low_battery: data?.summary?.technical_issues?.low_battery,
                    })}
                  </li>
                )}
                {(data?.summary?.pest_records?.total || 0) > 0 && (
                  <li>
                    <strong>{t('DigestReport.page-body.PestRecords')}: </strong>
                    {t('DigestReport.page-body.PestRecordsData', {
                      total: data?.summary?.pest_records?.total,
                      pests: data?.summary?.pest_records?.pests,
                      signs: data?.summary?.pest_records?.signs,
                    })}
                  </li>
                )}
                {(data?.summary?.vulnerabilities?.total || 0) > 0 && (
                  <li>
                    <strong>{t('DigestReport.page-body.VulnerabilitiesPCCs')}: </strong>
                    {t('DigestReport.page-body.VulnerabilitiesPCCsData', {
                      total: data?.summary?.vulnerabilities?.total,
                      sanitation: data?.summary?.vulnerabilities?.sanitation,
                      structural: data?.summary?.vulnerabilities?.structural,
                    })}
                  </li>
                )}
                {(data?.summary?.overdue_items?.total || 0) > 0 && (
                  <li>
                    <strong>{t('DigestReport.page-body.OverdueItems')}: </strong>
                    {t('DigestReport.page-body.OverdueItemsData', {
                      total: data?.summary?.overdue_items?.total,
                      vulnerabilities: data?.summary?.overdue_items?.vulnerabilities,
                    })}
                  </li>
                )}
              </ul>
            </div>

            <div className="mb-3">
              <h5 className="fw-bold border-top py-2 text-danger">{t('DigestReport.page-body.RequiresAttention')}</h5>
              <ul className="list-unstyled">
                {(data?.requires_attention?.captures?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.Captures')}:</strong>{' '}
                    {data?.requires_attention?.captures?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.triggers_without_capture?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.TriggersWithoutCapture')}:</strong>{' '}
                    {data?.requires_attention?.triggers_without_capture?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.bait_consumption?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.BaitModulesWithNewActivity')}:</strong>{' '}
                    {data?.requires_attention?.bait_consumption?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.visits_without_consumption?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.VisitsWithoutConsumption')}:</strong>{' '}
                    {data?.requires_attention?.visits_without_consumption?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.low_bait?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.LowBait')}:</strong>{' '}
                    {data?.requires_attention?.low_bait?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.depleted_bait?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.DepletedBait')}:</strong>{' '}
                    {data?.requires_attention?.depleted_bait?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.bait_replacement_due?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.BaitReplacementDue')}:</strong>{' '}
                    {data?.requires_attention?.bait_replacement_due?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.offline?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.Offline')}:</strong>{' '}
                    {data?.requires_attention?.offline?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.low_battery?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.LowBattery')}:</strong>{' '}
                    {data?.requires_attention?.low_battery?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.signal_degraded?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.SignalDegraded')}:</strong>{' '}
                    {data?.requires_attention?.signal_degraded?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.intermittent_connection?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.IntermittentConnection')}:</strong>{' '}
                    {data?.requires_attention?.intermittent_connection?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.new_pest_records?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.NewPestRecords')}:</strong>{' '}
                    {data?.requires_attention?.new_pest_records?.join(', ')}
                  </li>
                )}
                {(data?.requires_attention?.overdue_vulnerabilities?.length || 0) > 0 && (
                  <li>
                    • <strong>{t('DigestReport.page-body.OverdueVulnerabilities')}:</strong>{' '}
                    {data?.requires_attention?.overdue_vulnerabilities?.join(', ')}
                  </li>
                )}
              </ul>
            </div>
            {data?.patterns_observed && data?.patterns_observed?.length > 0 && (
              <div className="mb-3">
                <h5 className="fw-bold border-top py-2">{t('DigestReport.page-body.PatternsObserved')}</h5>
                <ul className="list-unstyled">
                  {data?.patterns_observed.map((pattern: string, idx: number) => (
                    <li key={idx} className="mb-1">
                      • {pattern}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data?.trap_activity && (
              <div className="mb-3">
                <h5 className="fw-bold border-top py-2">{t('DigestReport.page-body.NewTrapActivity')}</h5>
                <div className="text-muted small mb-2">
                  {t('DigestReport.page-body.NewTrapActivityData', {
                    activity_considered_after: formatDate(data?.trap_activity?.activity_considered_after),
                    included_events_from: formatDate(data?.trap_activity?.included_events?.from),
                    included_events_to: formatDate(data?.trap_activity?.included_events?.to),
                  })}
                </div>
                <ul className="list-unstyled">
                  {data?.trap_activity?.captures?.map((trap: any, idx: number) => (
                    <li key={idx} className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>
                      • {t('DigestReport.page-body.NewTrapActivityDate', { id: trap?.trap_id })}:
                      {trap?.locations?.join(', ')}
                      {trap?.events
                        ?.map((e: any) => `\n   ${formatDate(e?.timestamp)} ${formatSide(e?.side)}`)
                        .join(';')}
                    </li>
                  ))}
                  {data?.trap_activity?.triggers_without_capture?.map((trap: any, idx: number) => (
                    <li key={idx} className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>
                      • {t('DigestReport.page-body.NewTrapActivityDateSec', { id: trap?.trap_id })} —{' '}
                      {trap?.locations?.join(', ')}
                      {trap?.events
                        ?.map((e: any) => `\n   ${formatDate(e?.timestamp)} ${formatSide(e?.side)}`)
                        .join('; ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data?.bait_module_activity && (
              <div className="mb-3">
                <h5 className="fw-bold border-top py-2">{t('DigestReport.page-body.BaitModuleActivity')}</h5>
                <div className="text-muted small mb-2">
                  {t('DigestReport.page-body.BaitModuleActivityData', {
                    activity_considered_after: formatDate(data?.bait_module_activity?.activity_considered_after),
                    included_events_from: formatDate(data?.bait_module_activity?.included_events?.from),
                    included_events_to: formatDate(data?.bait_module_activity?.included_events?.to),
                  })}
                </div>
                <ul className="list-unstyled">
                  {data?.bait_module_activity?.consumption?.map((mod: any, idx: number) => (
                    <li key={idx} className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>
                      • <strong>{mod.module_id}</strong> —
                      {t('DigestReport.page-body.BaitModuleActivityDataSec', {
                        count: mod?.consumption_events_count,
                        total: mod?.total_consumed_grams,
                        location: mod?.location,
                        events: mod?.events
                          ?.map((e: any) => `\n   • ${formatDate(e?.timestamp)} (${e?.consumed_grams} g)`)
                          .join(''),
                      })}
                    </li>
                  ))}
                  {data?.bait_module_activity?.visits_without_consumption?.map((mod: any, idx: number) => (
                    <li key={idx} className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>
                      • <strong>{mod.module_id}</strong> —{' '}
                      {t('DigestReport.page-body.BaitModuleActivityVisits', {
                        visit_count: mod?.visit_count,
                        location: mod?.location,
                        events: mod?.events?.map((e: any) => `\n   • ${formatDate(e?.timestamp)}`).join(''),
                      })}
                    </li>
                  ))}
                  {data?.bait_module_activity?.replacement_due?.map((mod: any, idx: number) => (
                    <li key={idx} className="mb-1">
                      • <strong>{mod.module_id}</strong> —{' '}
                      {t('DigestReport.page-body.BaitModuleActivityReplace', {
                        location: mod?.location,
                        due_date: formatDate(mod?.due_date, false),
                      })}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data?.technical_issues && (
              <div className="mb-3">
                <h5 className="fw-bold border-top py-2">{t('DigestReport.page-body.TechnicalIssuesHeader')}</h5>
                <div className="text-muted small mb-2">{t('DigestReport.page-body.TechnicalIssuesSubtext')}</div>

                <h6 className="fw-bold mt-3">{t('DigestReport.page-body.CurrentUnresolvedIssues')}</h6>
                <ul className="list-unstyled">
                  {data?.technical_issues?.current_unresolved_issues?.map((issue: any, idx: number) => (
                    <li key={idx}>
                      • <strong>{issue.device_id}</strong> —{' '}
                      {issue.issue_type === 'offline' &&
                        t('DigestReport.page-body.IssueOfflineSince', { date: formatDate(issue?.since || '') })}
                      {issue.issue_type === 'low_battery' && t('DigestReport.page-body.IssueLowBattery')}
                      {issue.issue_type === 'signal_degraded' && t('DigestReport.page-body.IssueSignalDegraded')}
                      {issue.issue_type === 'intermittent_connection' &&
                        t('DigestReport.page-body.IssueIntermittentConnection')}
                    </li>
                  ))}
                  <li>
                    • <strong>{t('DigestReport.page-body.Hub')}</strong> —{' '}
                    {data?.technical_issues?.hub_statuses?.[0]?.status === 'online'
                      ? t('DigestReport.page-body.Online')
                      : t('DigestReport.page-body.OfflineStatus')}
                    ,{' '}
                    {t('DigestReport.page-body.HubDevicesReporting', {
                      reporting: data?.technical_issues?.hub_statuses?.[0]?.devices_reporting_recently,
                      expected: data?.technical_issues?.hub_statuses?.[0]?.devices_expected,
                    })}
                  </li>
                </ul>

                <h6 className="fw-bold mt-3">{t('DigestReport.page-body.HubOfflinePeriods')}</h6>
                <ul className="list-unstyled">
                  {data?.technical_issues?.hub_offline_periods?.map((period: any, idx: number) => (
                    <li key={idx}>
                      • {formatDate(period?.from)} – {formatDate(period?.to)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data?.pest_records && (
              <div className="mb-3">
                <h5 className="fw-bold border-top py-2">{t('DigestReport.page-body.PestRecordsHeader')}</h5>
                <div className="text-muted small mb-2">
                  {t('DigestReport.page-body.ChangesConsideredAfter', {
                    date: formatDate(data?.pest_records?.changes_considered_after),
                    from: data?.pest_records?.included_records?.from,
                    to: data?.pest_records?.included_records?.to,
                  })}
                </div>

                <h6 className="fw-bold mt-3 text-success">{t('DigestReport.page-body.New')}</h6>
                <ul className="list-unstyled">
                  {data?.pest_records?.new?.map((record: any, idx: number) => (
                    <li key={idx}>
                      • <strong>{record.record_id}</strong> —{' '}
                      {t('DigestReport.page-body.PestRecordNew', {
                        title: record?.title,
                        location: record?.location,
                        date: formatDate(record?.logged_at),
                      })}
                    </li>
                  ))}
                </ul>

                <h6 className="fw-bold mt-3 text-warning">{t('DigestReport.page-body.StillOpen')}</h6>
                <ul className="list-unstyled">
                  {data?.pest_records?.still_open?.map((record: any, idx: number) => (
                    <li key={idx}>
                      • <strong>{record.record_id}</strong> —{' '}
                      {t('DigestReport.page-body.PestRecordStillOpen', {
                        title: record?.title,
                        location: record?.location,
                        date: formatDate(record?.open_since),
                      })}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data?.vulnerabilities && (
              <div className="mb-3">
                <h5 className="fw-bold border-top py-2">{t('DigestReport.page-body.VulnerabilitiesHeader')}</h5>
                <div className="text-muted small mb-2">
                  {t('DigestReport.page-body.ChangesConsideredAfter', {
                    date: formatDate(data?.vulnerabilities?.changes_considered_after),
                    from: data?.vulnerabilities?.included_records?.from,
                    to: data?.vulnerabilities?.included_records?.to,
                  })}
                </div>

                <h6 className="fw-bold mt-3 text-success">{t('DigestReport.page-body.New')}</h6>
                <ul className="list-unstyled">
                  {data?.vulnerabilities?.new?.map((vul: any, idx: number) => (
                    <li key={idx}>
                      • <strong>{vul.vulnerability_id}</strong> —{' '}
                      {t('DigestReport.page-body.VulnerabilityNew', {
                        category: vul?.category,
                        issue: vul?.issue,
                        location: vul?.location,
                        responsible: vul?.responsible_party,
                        criticality: vul?.criticality,
                        date: formatDate(vul?.logged_at),
                      })}
                    </li>
                  ))}
                </ul>

                <h6 className="fw-bold mt-3 text-danger">{t('DigestReport.page-body.Overdue')}</h6>
                <ul className="list-unstyled">
                  {data?.vulnerabilities?.overdue?.map((vul: any, idx: number) => (
                    <li key={idx}>
                      • <strong>{vul.vulnerability_id}</strong> —{' '}
                      {t('DigestReport.page-body.VulnerabilityOverdue', {
                        category: vul?.category,
                        issue: vul?.issue,
                        location: vul?.location,
                        responsible: vul?.responsible_party,
                        criticality: vul?.criticality,
                        days: vul?.overdue_days,
                      })}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default DigestReport;
